import { toCents, fromCents } from "../utils/currencyUtils";
import {
  amountInWords,
  formatCheckDate,
  formatCheckNumericAmount,
} from "./amountInWords";

export const CHECK_GROUPING = {
  perPrize: "perPrize",
  perBowler: "perBowler",
};

const formatPlace = (position) => {
  if (position === null || position === undefined || position === "") return "";
  if (typeof position !== "number") return String(position);
  const lastTwo = position % 100;
  const last = position % 10;
  let suffix = "th";
  if (lastTwo < 11 || lastTwo > 13) {
    if (last === 1) suffix = "st";
    else if (last === 2) suffix = "nd";
    else if (last === 3) suffix = "rd";
  }
  return `${position}${suffix}`;
};

export const splitAmountAcrossBowlers = (amount, count) => {
  if (!count || count < 1) return [];
  const totalCents = toCents(amount || 0);
  if (!Number.isFinite(totalCents) || totalCents <= 0) return [];
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;
  return Array.from({ length: count }, (_, index) =>
    fromCents(base + (index < remainder ? 1 : 0))
  );
};

const prizeLineFromRow = (row) => ({
  eventName: row.eventName || "",
  place: row.place || "",
  amount: row.amount,
  teamName: row.teamName || "",
  squadName: row.squadName || "",
  bowlingCenterName: row.bowlingCenterName || "",
  squadDescription: row.squadDescription || "",
  squadDate: row.squadDate || "",
  squadTime: row.squadTime || "",
});

const locationFromResult = (result) => ({
  squadName: result.squadName || "",
  bowlingCenterName:
    result.bowlingCenterName || result.centerName || "",
  squadDescription: result.squadDescription || result.squad?.description || "",
  squadDate: result.squadDate || result.squad?.squadDate || "",
  squadTime: result.squadTime || result.squad?.squadTime || "",
  teamId: result.teamId || result.teamID || "",
});

const prizeDescription = (place, eventName) => {
  const parts = [place, eventName].filter(Boolean);
  if (parts.length === 2) return `${parts[0]} Place — ${parts[1]}`;
  return parts.join(" — ");
};

const isIndividualResult = (result) => Boolean(result?.isIndividualEvent);

const teamBowlers = (result) => {
  if (Array.isArray(result?.bowlers) && result.bowlers.length) {
    return result.bowlers
      .map((bowler) => ({
        bowlerId: bowler.id || bowler.bowlerId,
        bowlerName: bowler.name || bowler.bowlerName,
      }))
      .filter((bowler) => bowler.bowlerId && bowler.bowlerName);
  }
  if (result?.bowlerId && result?.bowlerName) {
    return [{ bowlerId: result.bowlerId, bowlerName: result.bowlerName }];
  }
  return [];
};

export const buildCheckValues = (row) => ({
  payeeName: row.payeeName || "",
  amount: formatCheckNumericAmount(row.amount),
  amountInWords: amountInWords(row.amount),
  checkDate: formatCheckDate(row.checkDate),
  tournamentName: row.tournamentName || "",
  tournamentDate: formatCheckDate(row.tournamentDate),
  place: row.place || "",
  eventName: row.eventName || "",
  teamName: row.teamName || "",
  prizeDescription: row.prizeDescription || "",
  memo: row.memo || row.prizeDescription || "",
  prizes: row.prizes || [],
  customText: "",
});

export const buildPrizeCheckRows = ({
  results = [],
  grouping = CHECK_GROUPING.perPrize,
  tournamentName = "",
  tournamentDate = "",
  checkDate = "",
}) => {
  const prizeRows = [];

  results.forEach((result) => {
    const prize = Number(result.prize || 0);
    if (!Number.isFinite(prize) || prize <= 0) return;

    const place = formatPlace(result.position);
    const eventName = result.eventName || "";
    const description = prizeDescription(place, eventName);

    if (isIndividualResult(result)) {
      if (!result.bowlerId || !result.bowlerName) return;
      prizeRows.push({
        bowlerId: result.bowlerId,
        payeeName: result.bowlerName,
        amount: prize,
        place,
        eventName,
        teamName: "",
        prizeDescription: description,
        memo: description,
        ...locationFromResult(result),
      });
      return;
    }

    const bowlers = teamBowlers(result);
    if (!bowlers.length) return;
    const shares = splitAmountAcrossBowlers(prize, bowlers.length);
    bowlers.forEach((bowler, index) => {
      const share = shares[index];
      if (!share) return;
      prizeRows.push({
        bowlerId: bowler.bowlerId,
        payeeName: bowler.bowlerName,
        amount: share,
        place,
        eventName,
        teamName: result.teamName || "",
        prizeDescription: description,
        memo: description,
        ...locationFromResult(result),
      });
    });
  });

  const withTournament = prizeRows.map((row) => ({
    ...row,
    tournamentName,
    tournamentDate,
    checkDate,
    prizes: [prizeLineFromRow(row)],
  }));

  if (grouping !== CHECK_GROUPING.perBowler) {
    return withTournament.filter((row) => row.payeeName && row.amount > 0);
  }

  const byBowler = new Map();
  withTournament.forEach((row) => {
    const current = byBowler.get(row.bowlerId) || {
      ...row,
      amount: 0,
      place: "",
      eventName: "",
      teamName: "",
      descriptions: [],
      prizes: [],
    };
    current.amount = fromCents(toCents(current.amount) + toCents(row.amount));
    current.descriptions.push(row.prizeDescription);
    current.prizes = [...(current.prizes || []), ...(row.prizes || [])];
    if (row.teamName && !current.teamName) current.teamName = row.teamName;
    byBowler.set(row.bowlerId, current);
  });

  return Array.from(byBowler.values())
    .map((row) => {
      const joined = row.descriptions.filter(Boolean).join("; ");
      return {
        bowlerId: row.bowlerId,
        payeeName: row.payeeName,
        amount: row.amount,
        place: "",
        eventName: "",
        teamName: row.teamName || "",
        prizeDescription: joined,
        memo: joined,
        prizes: row.prizes || [],
        tournamentName,
        tournamentDate,
        checkDate,
      };
    })
    .filter((row) => row.payeeName && row.amount > 0);
};
